import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import { AboutPage } from '@/types/sanity';

export const client = createClient({
	projectId: import.meta.env.VITE_SANITY_PROJECT_ID || 'your-project-id',
	dataset: import.meta.env.VITE_SANITY_DATASET || 'production',
	useCdn: true, // Set to `false` for development
	apiVersion: '2025-03-06', // Use a current date
});

// Set up image URL builder
const builder = imageUrlBuilder(client);

export function urlFor(source: SanityImageSource) {
	return builder.image(source);
}

// Site Settings
export async function getSiteSettings() {
	return client.fetch(`*[_type == "siteSettings"][0]`);
}

// Data fetching functions
export async function getAboutPage(): Promise<AboutPage | null> {
	return client.fetch(`*[_type == "aboutPage"][0]{
    _id,
    title,
    header {
      heading,
      subheading,
      backgroundImage
    },
    introduction,
    founder {
      name,
      role,
      image,
      bio
    },
    impactMap {
      title,
      subtitle,
      locations[] {
        country,
        latitude,
        longitude
      }
    },
    faq
  }`);
}

// Workshop fetching functions
export async function getWorkshops() {
	return client.fetch(`*[_type == "workshopPage"]{
    _id,
    title,
    slug,
    description,
    date,
    time,
    duration,
    location,
    variant,
    price,
    limit,
    "image": image.asset->url,
    calendarIconUrl,
    benefits,
    "id": slug.current
  }`);
}

export async function getWorkshopBySlug(slug: string) {
	return client.fetch(
		`*[_type == "workshopPage" && slug.current == $slug][0]{
    _id,
    title,
    slug,
    description,
    content,
    date,
    time,
    duration,
    location,
    variant,
    price,
    limit,
    "image": image.asset->url,
    calendarIconUrl,
    benefits,
    "id": slug.current
  }`,
		{ slug },
	);
}

// Blog post fetching functions
export async function getBlogPosts() {
	return client.fetch(`*[_type == "blogPost"] | order(publishedAt desc) {
    _id,
    title,
    slug,
    excerpt,
    author,
    category,
    "imageUrl": featuredImage.asset->url,
    "published": publishedAt,
    readTime,
    isFeatured,
    "id": slug.current
  }`);
}

export async function getBlogPostBySlug(slug: string) {
	return client.fetch(
		`*[_type == "blogPost" && slug.current == $slug][0]{
    _id,
    title,
    slug,
    excerpt,
    author,
    category,
    "imageUrl": featuredImage.asset->url,
    "published": publishedAt,
    readTime,
    content,
    "id": slug.current
  }`,
		{ slug },
	);
}

// Fetch Services page content
export async function getServicesPage() {
	return client.fetch(`*[_type == "servicesPage"][0]{
		_id,
		title,
		header {
			title,
			subtitle,
			description,
			"backgroundImage": backgroundImage.asset->url,
			overlayOpacity
		},
		services[] {
			_id,
			title,
			description,
			"image": image.asset->url,
			features[],
			slug
		},
		seo
	}`);
}

// Fetch Contact page content
export async function getContactPage() {
	return client.fetch(`*[_type == "contactPage"][0]{
    _id,
    title,
    header{
      title,
      subtitle,
      description,
      "backgroundImage": backgroundImage.asset->url,
      overlayOpacity
    },
    contactFormTitle,
    contactFormDescription,
    contactDetails,
    seo
  }`);
}

// Fetch Terms and Conditions page content
export async function getTermsPage() {
	return client.fetch(`*[_type == "termsPage"][0]{
    _id,
    title,
    header {
      title,
      subtitle,
      "backgroundImage": backgroundImage.asset->url,
      overlayOpacity
    },
    introduction,
    lastUpdated,
    terms[] {
      title,
      content
    },
    footer,
    seo
  }`);
}

// Fetch Privacy Policy page content
export async function getPrivacyPage() {
	return client.fetch(`*[_type == "privacyPage"][0]{
    _id,
    title,
    header {
      title,
      subtitle,
      "backgroundImage": backgroundImage.asset->url,
      overlayOpacity
    },
    introduction,
    lastUpdated,
    policies[] {
      title,
      content
    },
    footer,
    seo
  }`);
}

// Fetch testimonials
export async function getTestimonials() {
	return client.fetch(`*[_type == "testimonial"] | order(_createdAt desc) {
		_id,
		name,
		role,
		company,
		content,
		"imageUrl": image.asset->url,
		rating
	}`);
}

// Fetch partners
export async function getPartners() {
	return client.fetch(`*[_type == "partner"] | order(_createdAt desc) {
		_id,
		name,
		logo,
		website,
		description
	}`);
}

// Fetch stats
export async function getStats() {
	return client.fetch(`*[_type == "stats"] | order(_createdAt desc) {
		_id,
		title,
		value,
		description,
		icon
	}`);
}

// Fetch FAQ items
export async function getFaq() {
	return client.fetch(`*[_type == "faqItem"] | order(order asc) {
		_id,
		question,
		answer,
		category
	}`);
}
